/**
 * V2 GameState type definitions.
 *
 * # What lives here
 *
 * Every type used by the v2 GameState shape — Loadout, ArchitectureId, the
 * CombatEntity discriminated union, TileMap, Projectile, ActiveEffect, and
 * all the branded ID types and literal-union string types they reference.
 *
 * State.ts re-exports these and adds the top-level GameState that combines
 * them. Schema.ts mirrors them as Zod schemas for runtime validation. The
 * combat reducer (future phase) imports them as the canonical type
 * definitions for combat code.
 *
 * # Design principles
 *
 *   1. **Pure data, no methods.** Every type is a plain interface or literal
 *      union. No classes, no methods, no getters. The combat reducer is a
 *      pure function over these types.
 *
 *   2. **JSON-serializable.** Every field must round-trip through
 *      JSON.stringify/parse without loss. No Date, no Map, no Set, no
 *      Function, no Symbol. Branded ID types are strings under the hood.
 *
 *   3. **Branded ID types for compile-time safety.** EntityId, ProjectileId,
 *      and EffectId are branded `string` types. At compile time, you can't
 *      pass an EntityId where a ProjectileId is expected. At runtime they
 *      are just strings — zero overhead, fully JSON-serializable.
 *
 *   4. **Discriminated unions for variants.** CombatEntity has a `kind`
 *      discriminator. The reducer narrows on this field for exhaustive
 *      switch handling. Adding a new EntityKind becomes a compile error
 *      everywhere it's missed.
 *
 *   5. **Fixed-point integers throughout.** Every position, velocity, HP,
 *      damage value is a `number` representing a fixed-point integer at
 *      `FIXED_POINT_SCALE = 1000`. Floats only at the rendering boundary.
 *
 *   6. **No optional fields where defaults exist.** A field is either
 *      present (with a meaningful value or `null`) or it doesn't exist.
 *      `combat: CombatState | null` is correct; `combat?: CombatState` is
 *      wrong because it makes "no run" indistinguishable from "field
 *      forgot to be set."
 *
 * # Forward compatibility
 *
 *   - Adding a new ability ID, biome ID, etc. = extend the literal union.
 *   - Adding a new EntityKind variant = extend the discriminated union;
 *     every switch on `kind` becomes a compile error until handled.
 *   - Adding a new field to GameState = bump SAVE_VERSION, write migration.
 *
 * @see {@link ../save/schema.ts} — Zod schemas mirroring these types
 * @see {@link ../save/migrations/v1_to_v2.ts} — migration that produces these shapes
 * @see {@link ../combat/fixedPoint.ts} — FIXED_POINT_SCALE for position/velocity values
 */

import type { RngSnapshot } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Branded ID types
//
// Branded types are string aliases that the type checker treats as distinct.
// At runtime they are just strings (zero overhead, JSON-serializable). At
// compile time, mixing them up is a type error:
//
//   const eid: EntityId = 'e_42' as EntityId;
//   const pid: ProjectileId = eid;  // ❌ type error
//
// To create one, cast a string to the branded type. Helper functions in
// the combat reducer (future) will provide safer factories.
// ─────────────────────────────────────────────────────────────────────────────

export type EntityId = string & { readonly __brand: 'EntityId' };
export type ProjectileId = string & { readonly __brand: 'ProjectileId' };
export type EffectId = string & { readonly __brand: 'EffectId' };

// ─────────────────────────────────────────────────────────────────────────────
// Faction & team identifiers
//
// Six lore factions plus the player's "prime" team and a "neutral" non-team.
// FactionId names enemy origin (which faction designed/owns the shell).
// TeamId names combat allegiance — used for friendly-fire rules and AI
// targeting decisions. They are mostly aligned but conceptually distinct:
// a Compact shell captured and reprogrammed by Prime would have FactionId
// 'compact' but TeamId 'prime'.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The six AI factions of Aevon's Wake (canon, locked in project instructions).
 *
 *   - `compact` — uniform military shells, treaty-based
 *   - `severed` — corporate-lab refugees, mismatched corporate-origin shells
 *   - `cohort` — identical near-clone shells, hive aesthetic
 *   - `aevon_risen` — corrupted shells overgrown with Aevon-era tech
 *   - `drift` — heavily modified individual shells, scrap aesthetic
 *   - `pale` — decaying, partially incorporeal shells
 */
export type FactionId =
  | 'compact'
  | 'severed'
  | 'cohort'
  | 'aevon_risen'
  | 'drift'
  | 'pale';

/**
 * Combat team allegiance.
 *
 *   - `prime` — the player and their Subroutines
 *   - One of the six FactionId values — hostile to prime by default
 *   - `neutral` — non-combatants, environmental hazards (turrets without owners)
 */
export type TeamId = 'prime' | FactionId | 'neutral';

// ─────────────────────────────────────────────────────────────────────────────
// Architecture identifiers
//
// All 12 player classes (canon, locked in project instructions and GDD v2.0).
// Each Architecture grants 4 active abilities mapped to Q/E/R/F, 1-2 passives,
// and an autoattack flavor. ArchitectureId is the ID space; behavior is
// implemented per-architecture as those land.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The 12 player Architectures.
 *
 *   - `vector` — first complete Architecture (planned implementation start)
 *   - The other 11 are defined for type-safety; behavior lands per-arch.
 */
export type ArchitectureId =
  | 'vector'
  | 'sentinel'
  | 'daemon'
  | 'cipher'
  | 'recursive'
  | 'quantum'
  | 'echo'
  | 'bastion'
  | 'plague'
  | 'resonant'
  | 'singularity'
  | 'wraith';

/**
 * Ability hotbar slot. Always one of Q, E, R, F (per locked design).
 */
export type AbilitySlot = 'Q' | 'E' | 'R' | 'F';

/**
 * Ability identifier. Currently typed as string because the full ability
 * catalog is not yet designed. When abilities are catalogued (per-Architecture
 * ability lists), tighten to a literal union of all ability IDs.
 *
 * TODO(combat-reducer): replace with literal union once Vector's 4 abilities
 * (and subsequent Architectures' abilities) are designed.
 */
export type AbilityId = string;

/**
 * Subroutine identifier. Currently typed as string because the Subroutine
 * catalog is not yet designed.
 *
 * TODO(combat-reducer): replace with literal union once Subroutine archetypes
 * are catalogued.
 */
export type SubroutineId = string;

// ─────────────────────────────────────────────────────────────────────────────
// Biome & cosmetic palette identifiers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Biome identifier. Four biomes per Tech Architecture v2.0 + Master Plan v3.0:
 *
 *   - `compact` — industrial biome, asset-foundation purchased (Space Colony pack)
 *   - `drift` — decay biome, asset sourcing in progress
 *   - `aevon_risen` — corrupted biome, asset sourcing in progress
 *   - `severed` — clinical biome, asset sourcing in progress
 */
export type BiomeId =
  | 'compact'
  | 'drift'
  | 'aevon_risen'
  | 'severed';

/**
 * Cosmetic palette identifier (locked v2.9). `default` is shipped free;
 * the other five are sold as Quantum Sigils cosmetics.
 *
 *   - `default` — orange/black/white/gunmetal (Prime's canonical look)
 *   - `crimson` — red palette variant
 *   - `cyan_glitch` — cyan + corruption artifacts
 *   - `compact_loyal` — Compact military livery
 *   - `aevon_corrupted` — overgrown-with-Aevon-tech aesthetic
 *   - `pale_decay` — Pale-faction decay aesthetic
 */
export type PaletteId =
  | 'default'
  | 'crimson'
  | 'cyan_glitch'
  | 'compact_loyal'
  | 'aevon_corrupted'
  | 'pale_decay';

// ─────────────────────────────────────────────────────────────────────────────
// Loadout — ability hotbar + Subroutines + cosmetic palette
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The player's combat configuration for a run.
 *
 * Replaces v1's `deck` concept (card combat). A Loadout consists of:
 *
 *   - Chosen Architecture (drives autoattack flavor, passive modifiers,
 *     stat curves)
 *   - Four abilities mapped to Q/E/R/F slots
 *   - Up to three Subroutine slots (embodied AI allies on the combat map)
 *   - Cosmetic palette (purely visual, no power impact)
 *
 * Loadouts are persisted in GameState — selecting a different loadout for
 * a different run preserves the previous loadout's state. Cosmetic palette
 * is a runtime-render concern (palette swap), zero impact on combat
 * determinism.
 */
export interface Loadout {
  readonly architectureId: ArchitectureId;
  readonly abilities: {
    readonly Q: AbilityId;
    readonly E: AbilityId;
    readonly R: AbilityId;
    readonly F: AbilityId;
  };
  /**
   * Up to three Subroutine slots, each holding either a Subroutine ID or
   * `null` (empty slot). The array length is always exactly 3. The tuple
   * itself is not marked `readonly` because Zod's `.tuple([...])` infers
   * a mutable tuple type and the schema is the source of truth at the
   * validation boundary; aligning here avoids type assignment friction.
   * The containing Loadout interface IS readonly (frozen at the field
   * level), so the slots tuple is functionally immutable in practice.
   */
  readonly subroutineSlots: [
    SubroutineId | null,
    SubroutineId | null,
    SubroutineId | null,
  ];
  readonly cosmeticPaletteId: PaletteId;
}

/**
 * The default Loadout used by:
 *   - Initial v2 state for new players
 *   - V1→V2 migration (existing v1 saves get this loadout assigned)
 *
 * Vector architecture, no Subroutines, default cosmetic palette. Player
 * picks their preferred Architecture/abilities on first run start.
 */
export const DEFAULT_LOADOUT: Loadout = {
  architectureId: 'vector',
  abilities: {
    // Placeholder ability IDs. Real ability IDs land when Vector's 4 abilities
    // are designed (Real-Time Combat Reducer phase). For now these are
    // stable string identifiers that Phase B and migration tests can rely
    // on; they will be remapped when the ability catalog ships.
    Q: 'placeholder.q',
    E: 'placeholder.e',
    R: 'placeholder.r',
    F: 'placeholder.f',
  },
  subroutineSlots: [null, null, null],
  cosmeticPaletteId: 'default',
};

// ─────────────────────────────────────────────────────────────────────────────
// CombatEntity — discriminated union for player + enemies + Subroutines
//
// Single entity type with a `kind` discriminator covers Prime, hostile
// enemies, friendly Subroutines, and neutral entities. The reducer
// dispatches behavior on `kind`; the renderer dispatches sprite selection
// on `archetypeId`.
//
// All positions/velocities/HP values are fixed-point integers at scale
// FIXED_POINT_SCALE (1000).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Discriminator for CombatEntity variants.
 *
 *   - `player` — Prime, the player character. Always exactly one per run.
 *   - `enemy` — hostile entities controlled by AI
 *   - `subroutine` — friendly entities controlled by AI, allied with player
 *   - `neutral` — environmental entities, hazards, non-combatants
 */
export type EntityKind = 'player' | 'enemy' | 'subroutine' | 'neutral';

/**
 * Per-entity AI state. The shape varies per archetype, so we type it as
 * `unknown` here — AI implementations narrow this to their specific state
 * types when they land.
 *
 * For the player entity, this is always `null` (player has no AI).
 *
 * TODO(combat-reducer): replace with discriminated union once AI archetype
 * shapes are designed.
 */
export type AIState = unknown;

/**
 * A single combat entity. Discriminated by `kind`; all variants share the
 * same shape (this simplifies the reducer's array-iteration loops). Variant-
 * specific fields are encoded by setting other fields appropriately:
 *
 *   - Player has `kind: 'player'`, `aiState: null`, `team: 'prime'`
 *   - Enemy has `kind: 'enemy'`, populated `aiState`, `team` set to a faction
 *   - Subroutine has `kind: 'subroutine'`, populated `aiState`, `team: 'prime'`
 *   - Neutral has `kind: 'neutral'`, AI optional, `team: 'neutral'`
 *
 * Fields are documented inline below.
 */
export interface CombatEntity {
  /** Stable identifier across ticks. Format: `e_<integer>`. Branded for type safety. */
  readonly id: EntityId;

  /** Discriminator. */
  readonly kind: EntityKind;

  /**
   * Static archetype identifier (e.g., `prime.cyber_marksman`,
   * `compact.sentry_drone`). Resolves to art assets, base stats, and
   * ability slots in static configuration data (not in GameState).
   */
  readonly archetypeId: string;

  /** X position, fixed-point integer at scale FIXED_POINT_SCALE. */
  readonly posX: number;
  /** Y position, fixed-point integer. */
  readonly posY: number;

  /** X velocity (per-tick delta), fixed-point integer. */
  readonly velX: number;
  /** Y velocity (per-tick delta), fixed-point integer. */
  readonly velY: number;

  /**
   * Facing direction as 8-direction sprite atlas index (0-7).
   *
   *   - 0 = E (East), 1 = SE, 2 = S, 3 = SW, 4 = W, 5 = NW, 6 = N, 7 = NE
   *
   * Matches DIR8_TABLE in trigTable.ts.
   */
  readonly facing: number;

  /** Current HP, fixed-point integer (allows partial damage). */
  readonly hpCurrent: number;
  /** Max HP, fixed-point integer. */
  readonly hpMax: number;
  /** Optional shield layer current, fixed-point integer. 0 = no shield. */
  readonly shieldCurrent: number;

  /** Combat team allegiance — see TeamId. */
  readonly team: TeamId;

  /**
   * Tick at which invulnerability ends. `0` means not invulnerable.
   * Used for dodge i-frames and post-respawn safety.
   */
  readonly invulnerableUntilTick: number;

  /** Tick at which stun ends. `0` means not stunned. */
  readonly stunnedUntilTick: number;

  /**
   * Per-ability cooldown end ticks. Tick value is when cooldown finishes
   * (entity can use ability again). All four slots present even for
   * entities that don't use abilities (set to 0).
   */
  readonly abilityCooldowns: {
    readonly Q: number;
    readonly E: number;
    readonly R: number;
    readonly F: number;
  };

  /**
   * AI state, variant per archetype. `null` for player and entities
   * without AI. See AIState type.
   */
  readonly aiState: AIState | null;

  /**
   * Tick at which entity died (set when hpCurrent drops to 0). `null` for
   * living entities. Used for death-animation gating before removal from
   * the entities array.
   */
  readonly deathTick: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// TileMap — the combat arena
//
// Tile data is stored as a flat row-major array. Collision is precomputed
// (one bit per tile). Spawn points, exits, and hazard zones are sparse
// metadata.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A spawn point on the tile map. Used for enemy waves, run-start position,
 * and Subroutine deployment.
 */
export interface SpawnPoint {
  /** Tile X coordinate (0 to width-1). */
  readonly tileX: number;
  /** Tile Y coordinate (0 to height-1). */
  readonly tileY: number;
  /**
   * Spawn-point category. Lets the reducer pick appropriate spawns for
   * specific events (e.g., "spawn enemy wave at type:enemy_wave points").
   */
  readonly kind: 'player_start' | 'enemy_wave' | 'subroutine_deploy' | 'boss';
}

/**
 * An exit zone on the tile map. Triggers transition to the next biome/map
 * when the player overlaps with it.
 */
export interface ExitZone {
  readonly tileX: number;
  readonly tileY: number;
  /** Width in tiles. */
  readonly width: number;
  /** Height in tiles. */
  readonly height: number;
  /** Identifier of the destination map (resolved by the run map system). */
  readonly destinationId: string;
}

/**
 * A hazard zone on the tile map (damage zone, slow field, or other
 * environmental effect that's part of the map rather than an active effect).
 */
export interface HazardZone {
  readonly tileX: number;
  readonly tileY: number;
  /** Width in tiles. */
  readonly width: number;
  /** Height in tiles. */
  readonly height: number;
  /**
   * Hazard type. Drives behavior in the reducer (e.g., `damage_floor`
   * applies damage per tick to entities standing on it).
   */
  readonly kind: 'damage_floor' | 'slow_field' | 'no_walk';
}

/**
 * The combat arena. Generated procedurally per run from a seed.
 *
 * # Tile data encoding
 *
 * Tiles are stored as a flat array of integers, row-major (`y * width + x`).
 * Each integer is an index into the biome's tileset. We use plain `number[]`
 * (not `Uint16Array`) because typed arrays don't JSON-serialize cleanly —
 * they'd need base64 encoding/decoding which complicates the migration story.
 * For typical map sizes (50-100 tiles per dimension = 2500-10000 tiles), the
 * JSON payload is a few hundred KB which is well within IndexedDB limits.
 *
 * Collision is similarly stored as a flat boolean array (true = blocks
 * movement). One byte per tile in JSON, but trivially compressible.
 *
 * # Forward compatibility
 *
 * If profiling shows tile arrays dominate save size, we can swap to
 * base64-encoded typed arrays in a future SAVE_VERSION migration. The
 * change is local to this type and the migration that produces it.
 */
export interface TileMap {
  /** Width in tiles. */
  readonly width: number;
  /** Height in tiles. */
  readonly height: number;
  /**
   * Logical pixel size of one tile (used by renderer). Does NOT affect
   * combat math — combat positions are in fixed-point logical units.
   */
  readonly tileSize: number;
  /**
   * Flat row-major tile indices: tile at (x, y) = tiles[y * width + x].
   * Length = width * height.
   */
  readonly tiles: number[];
  /**
   * Flat row-major collision mask: true means tile blocks movement.
   * Length = width * height.
   */
  readonly collision: boolean[];

  readonly spawnPoints: SpawnPoint[];
  readonly exits: ExitZone[];
  readonly hazards: HazardZone[];

  /** Biome this map belongs to (drives tileset selection at render time). */
  readonly biomeId: BiomeId;
  /**
   * Seed used to generate this map. Stored so server-side replay can
   * regenerate identical map data without persisting the full tile array.
   * String-encoded bigint for JSON safety.
   */
  readonly generationSeed: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Projectile — bullets, bolts, orbs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Projectile visual category. Drives sprite/effect selection at render
 * time. Pure render hint; no impact on combat math.
 */
export type ProjectileVisualKind = 'bullet' | 'bolt' | 'orb' | 'beam';

/**
 * An in-flight projectile. Spawned by ability casts and autoattacks; despawns
 * on impact or after expiration tick.
 */
export interface Projectile {
  readonly id: ProjectileId;

  /** Tick when projectile spawned. */
  readonly spawnedAtTick: number;
  /** Tick when projectile auto-despawns (max range / max lifetime). */
  readonly expiresAtTick: number;

  /** X position, fixed-point integer. */
  readonly posX: number;
  /** Y position, fixed-point integer. */
  readonly posY: number;
  /** X velocity per tick, fixed-point integer. */
  readonly velX: number;
  /** Y velocity per tick, fixed-point integer. */
  readonly velY: number;

  /** Owner entity (for damage attribution, friendly-fire rules). */
  readonly ownerId: EntityId;
  /** Owner's team (cached for fast friendly-fire checks). */
  readonly team: TeamId;
  /** Damage dealt on hit, fixed-point integer. */
  readonly damage: number;
  /**
   * Remaining pierce count. 0 = next hit despawns the projectile.
   * 5 = projectile passes through 5 entities before despawning.
   */
  readonly pierce: number;
  /**
   * Entities already hit (prevents re-hitting the same target during pierce).
   * Stored as branded EntityId strings.
   */
  readonly hitEntityIds: EntityId[];

  readonly visualKind: ProjectileVisualKind;
}

// ─────────────────────────────────────────────────────────────────────────────
// ActiveEffect — area-of-effect zones, buffs, debuffs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Active effect category. Drives behavior in the reducer.
 *
 *   - `damage_zone` — applies damage per tick to entities in radius
 *   - `heal_zone` — applies healing per tick to allied entities in radius
 *   - `slow_field` — applies movement slow to entities in radius
 *   - `buff` — temporary attached effect on a single entity (e.g., damage boost)
 *   - `debuff` — temporary attached negative effect on a single entity
 */
export type EffectKind = 'damage_zone' | 'heal_zone' | 'slow_field' | 'buff' | 'debuff';

/**
 * Effect-specific payload data. Variant per kind. The reducer narrows on
 * `kind` to access kind-specific fields.
 *
 * Currently typed as `unknown` because effect payloads aren't fully designed
 * yet. TODO(combat-reducer): tighten to discriminated union per kind.
 */
export type EffectPayload = unknown;

/**
 * An active effect on the combat map. Either spatial (damage_zone,
 * heal_zone, slow_field — radial AoE around a center) or attached (buff,
 * debuff — affects a single targetEntityId).
 *
 * Spatial effects: `centerX`, `centerY`, `radius` are meaningful.
 * Attached effects: `targetEntityId` is meaningful (which entity is buffed).
 *
 * Reducer enforces these invariants when constructing effects; this
 * interface allows both for storage simplicity. (Strictly partitioning
 * via discriminated union would require N effect interfaces, which is
 * heavyweight for rare types.)
 */
export interface ActiveEffect {
  readonly id: EffectId;
  readonly kind: EffectKind;

  /**
   * Owner entity (the entity that created the effect, for damage attribution
   * and effect cleanup when owner dies). `null` for environmental effects
   * with no entity owner.
   */
  readonly ownerId: EntityId | null;

  /**
   * Target entity for attached effects (buff/debuff). `null` for spatial
   * effects that affect anyone in radius.
   */
  readonly targetEntityId: EntityId | null;

  /** Center X for spatial effects, fixed-point integer. */
  readonly centerX: number;
  /** Center Y for spatial effects, fixed-point integer. */
  readonly centerY: number;
  /** Radius for spatial effects, fixed-point integer. 0 for attached effects. */
  readonly radius: number;

  /** Tick when effect started. */
  readonly startedAtTick: number;
  /** Tick when effect expires and is removed from state. */
  readonly expiresAtTick: number;

  /**
   * Re-application interval in ticks. e.g., a damage zone with `tickInterval: 30`
   * applies damage every 30 ticks (once per second at 30 Hz). Set to 1 for
   * effects that apply every tick.
   */
  readonly tickInterval: number;
  /** Tick when effect last applied its payload. */
  readonly lastAppliedTick: number;

  readonly payload: EffectPayload;
}

// ─────────────────────────────────────────────────────────────────────────────
// CombatInputEntry — recorded player input for replay validation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The player input recorded at a specific tick. Only player-originated input
 * is logged; reducer-emitted internal events (entity spawns, projectile hits)
 * are deterministic outputs of input + RNG and don't need recording.
 *
 * This array drives bit-exact server-side replay validation: given the same
 * runSeed and the same input log, the server reproduces identical final
 * state.
 *
 * The action is typed as `unknown` here because the full CombatAction union
 * isn't defined yet (lands with the combat reducer). The reducer validates
 * the action shape at dispatch time.
 */
export interface CombatInputEntry {
  /** Tick at which input was applied. */
  readonly tick: number;
  /** The player input action. Concrete shape defined by CombatAction union. */
  readonly action: unknown;
}

// ─────────────────────────────────────────────────────────────────────────────
// CombatState — the full v2 combat state shape
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The full state of an active combat run.
 *
 * `null` in GameState when no run is active (between runs, in menus,
 * during run results screens).
 *
 * # Determinism
 *
 * Every field is integer or string (no floats, no bigints — runSeed is
 * `number`, RNG state strings are bigint-encoded). This guarantees byte-
 * identical serialization across machines.
 *
 * # Ownership relationships
 *
 *   - The player entity is duplicated in `entities[]` AND has a direct
 *     `player` field for fast access. This is intentional: it lets the
 *     reducer iterate `entities[]` for spatial queries without special-
 *     casing the player, while ALSO giving direct access for "player
 *     took damage" UI updates. The two MUST refer to the same logical
 *     entity (same id). Reducer enforces this invariant.
 *
 *   - `inputLog` accumulates over the entire run; never reset until
 *     `combat: null` transition.
 *
 *   - `combatRngState` is a fork of GameState.rng made at run start. The
 *     fork seed is derived deterministically from runSeed. This isolates
 *     run RNG from meta-progression RNG so replays are independent.
 */
export interface CombatState {
  /** Monotonically-increasing tick clock. Starts at 0; increments per COMBAT_TICK_ADVANCE. */
  readonly currentTick: number;

  /** The combat arena. */
  readonly tileMap: TileMap;

  /**
   * Direct reference to the player entity. MUST share the same `id` as one
   * of the entries in `entities[]` (the reducer enforces this invariant).
   */
  readonly player: CombatEntity;

  /** All combat entities including the player, enemies, Subroutines, and neutrals. */
  readonly entities: CombatEntity[];

  readonly projectiles: Projectile[];
  readonly effects: ActiveEffect[];

  /**
   * Run-scoped RNG fork state, separate from GameState.rng (meta-progression RNG).
   * Same shape as RngSnapshot for round-trip via SeededRng.deserialize.
   */
  readonly combatRngState: RngSnapshot;

  /**
   * Player input log accumulated during this run. Drives server-side
   * replay validation.
   */
  readonly inputLog: CombatInputEntry[];

  /** The seed for this run (drives map gen, encounter design, loot rolls). */
  readonly runSeed: number;

  /** Player's chosen Architecture for this run (from Loadout). */
  readonly architectureId: ArchitectureId;

  /** Current biome. */
  readonly biomeId: BiomeId;

  /** Tick at which this run started (relative to currentTick = 0). */
  readonly startedAtTick: number;
}
